import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchAndSetBoundaries,
  fetchAndSetCentroids,
} from '../../common/commonSlice'
import {
  clone,
  formatValues,
  isValidDataForColorScale,
  calculateColorScale,
  getNameFromCodelist,
  formatNumberOnUnitMeasure,
  formatUnitMeasure,
  getEndpointFromConfig,
  getSuppressedCountries,
  formatUnitMult,
} from '../../common/utils/helper'
import { DEFAULT_BIN_COLORS } from '../../common/utils/constants'
import WorldMap from '../../common/components/WorldMap/WorldMap'
import MapLegend from '../../common/components/WorldMap/MapLegend'
import {
  endpointLabels,
  LegendType,
  MissingDataMapTypes,
  ScaleTypes,
} from '../../common/utils/enum'
import { COLOR_MAP } from '../../common/utils/theme'
import ContinuousMapLegend from '../../common/components/WorldMap/ContinuousMapLegend'
import StandardDeviationLegend from '../../common/components/WorldMap/StandardDeviationLegend'

const WorldMapWrapper = ({
  filteredIndicatorData,
  selectedSingleYear,
  indicatorCode,
  visualization,
  uniqueKey,
  selectedCountries = [],
  countryDropdownValue,
  isTopic = false,
  isTheme = false,
}) => {
  const [features, setFeatures] = useState([])

  const boundaries = useSelector((state) => state.common['countryBoundaries'])
  const countryCentroids = useSelector((state) => state.common.countryCentroids)
  const countryList = useSelector((state) => state.common['countryList'])
  const { codeList, featureProps } = useSelector((state) => state.common)
  const { mapApiKey, endpointList } = featureProps

  const dispatch = useDispatch()

  useEffect(() => {
    const fetchCountryData = async () => {
      const fetchCountriesBoundries = async () => {
        const mapApiUrl = getEndpointFromConfig(
          endpointList,
          endpointLabels.MapBoundaries
        )?.apiEndpoint

        dispatch(
          fetchAndSetBoundaries({
            endpoint: mapApiUrl,
            headers: {},
          })
        )
      }
      fetchCountriesBoundries()
    }

    const fetchCentroids = async () => {
      const fetchCountriesCentroids = async () => {
        const centroidsApiUrl = getEndpointFromConfig(
          endpointList,
          endpointLabels.MapCentroids
        )?.apiEndpoint
        dispatch(
          fetchAndSetCentroids({
            endpoint: centroidsApiUrl,
            headers: {},
          })
        )
      }
      fetchCountriesCentroids()
    }

    fetchCountryData()
    fetchCentroids()
  }, [])

  useEffect(() => {
    if (Object.keys(boundaries).length > 0) {
      let initialFeatures = JSON.parse(JSON.stringify(boundaries?.features))

      if (filteredIndicatorData) {
        let indicatorDataPerYear = clone(filteredIndicatorData)

        indicatorDataPerYear.forEach((data) => {
          const year = data?.data?.xAxis?.[0]
          const value = data?.data?.yAxis?.[0]

          data.value =
            value && value !== 'null' && value !== 'NaN'
              ? formatValues(value)
              : null
          data.year = year

          const countryData = initialFeatures.find(
            (feature) => data.countryCode === feature.properties.ISO_A3
          )
          // suppression logic -- start
          const suppressedCountries = visualization?.suppression
          // const suppressedCountries = 'AUT'
          let isSuppressedCountry = getSuppressedCountries(
            suppressedCountries
          ).includes(data.countryCode)
          // suppression logic -- end
          if (countryData && !isSuppressedCountry) {
            countryData.properties = { ...countryData.properties, ...data }
          }
        })
      }
      setFeatures(initialFeatures)
    }
  }, [boundaries, filteredIndicatorData, selectedSingleYear, indicatorCode])

  // Color scheme for sequential, diverging, categorical --start
  let scaleType = ScaleTypes.Sequential
  if (visualization && visualization.scale_type) {
    if (visualization.scale_type.toLowerCase().includes('sequential reverse')) {
      scaleType = ScaleTypes.SequentialReverse
    } else {
      scaleType = visualization.scale_type
    }
  }

  let missingDataColor = ''
  if (
    visualization &&
    visualization.missing_data &&
    visualization.missing_data.length > 0 &&
    visualization.missing_data[0].map
  ) {
    const missingDataType = visualization.missing_data[0].map
    if (missingDataType === MissingDataMapTypes.SpecifyColor) {
      missingDataColor = visualization.missing_data[0].no_data_color
    }
  }

  let values = []
  // suppression logic -- start
  const suppressedCountries = visualization?.suppression
  // const suppressedCountries = 'AUT'
  let filteredCountries = countryList.filter(
    (item) => !getSuppressedCountries(suppressedCountries).includes(item.id)
  )
  // suppression logic -- end
  // calculating legend scale on filtered data
  if (scaleType === ScaleTypes.Categorical) {
    filteredIndicatorData.forEach((data) => {
      if (filteredCountries.map((item) => item.id).includes(data.countryCode)) {
        const value = data?.data?.yAxis?.[0]
        if (value && value !== 'null' && value !== 'NaN') {
          values.push(value)
        }
      }
    })
  } else {
    filteredIndicatorData.forEach((data) => {
      if (filteredCountries.map((item) => item.id).includes(data.countryCode)) {
        const value = data?.data?.yAxis?.[0]
        if (isValidDataForColorScale(value)) {
          const formattedValue = formatValues(value)
          values.push(formattedValue)
        }
      }
    })
  }

  let {
    legendType,
    binCount,
    legendRange,
    getColorScale,
    min,
    mid,
    max,
    binType,
    legendValuesForStd,
  } = calculateColorScale(
    scaleType,
    visualization,
    values,
    filteredIndicatorData[0]
  )
  // Color scheme for sequential, diverging, categorical --end

  const getUnitMeasureName = (data) => {
    if (data?.length) {
      const countryIndicatorData = data?.[0]
      const primaryUnitMeasure = countryIndicatorData?.unitMeasure
        ? getNameFromCodelist(
            codeList,
            'UNIT_MEASURE',
            countryIndicatorData?.unitMeasure
          )
        : ''

      return primaryUnitMeasure
    }
  }

  const unitMeasureName = getUnitMeasureName(filteredIndicatorData)

  const getTooltipContent = (d) => {
    if (!isValidDataForColorScale(d.properties.value)) {
      return `<div class="tooltip-top">
              <span class="tooltip-country">${d.properties.WB_NAME}</span>
              <span class="tooltip-year">${
                d.properties.year ? ` (${d.properties.year})` : ''
              }</span>
            </div>
            <div class="tooltip-bottom">
              <span class="tooltip-value">Data not available</span>
            </div>`
    } else {
      if (scaleType === ScaleTypes.Categorical) {
        const value = d.properties.value === 1 ? 'Yes' : 'No'
        return `<div class="tooltip-top">
                <span class="tooltip-country">${d.properties.WB_NAME}</span>
                <span class="tooltip-year">${
                  d.properties.year ? ` (${d.properties.year})` : ''
                }</span>
              </div>
              <div class="tooltip-bottom">
                <span class="tooltip-value">${value}${
          unitMeasureName ? ` ${unitMeasureName}` : ''
        }</span>
              </div>`
      } else {
        return `<div class="tooltip-top">
                <span class="tooltip-country">${d.properties.WB_NAME}</span>
                <span class="tooltip-year">${
                  d.properties.year ? ` (${d.properties.year})` : ''
                }</span>
              </div>
              <div class="tooltip-bottom">
                <span class="tooltip-value">${formatNumberOnUnitMeasure(
                  d.properties.value,
                  filteredIndicatorData?.[0]?.unitMeasure,
                  filteredIndicatorData?.[0]?.decimals
                )} ${formatUnitMult(
          codeList,
          filteredIndicatorData?.[0]?.unitMult
        )} ${formatUnitMeasure(
          filteredIndicatorData?.[0]?.unitMeasure,
          unitMeasureName
        )}</span>
              </div>`
      }
    }
  }

  return (
    <div id="worldmap_container" key={uniqueKey}>
      <WorldMap
        features={features}
        countryCentroids={countryCentroids}
        getColorScale={filteredIndicatorData ? getColorScale : undefined}
        getTooltipContent={getTooltipContent}
        indicatorCode={indicatorCode}
        key={indicatorCode}
        isTheme={true}
        selectedCountries={
          countryDropdownValue
            ? [...selectedCountries, countryDropdownValue]
            : selectedCountries
        }
        isTopic={isTopic}
        scaleType={scaleType}
        countryFocus={countryDropdownValue}
        missingDataColor={missingDataColor}
        binType={binType}
        legendRange={legendRange}
      />
      {scaleType === ScaleTypes.Categorical ||
      legendType === LegendType.Binned ? (
        <>
          {legendValuesForStd ? (
            <StandardDeviationLegend
              legendRange={legendRange}
              colors={
                scaleType === ScaleTypes.Categorical
                  ? COLOR_MAP['categorical']
                  : COLOR_MAP[`${scaleType.replace(' ', '')}_${binCount}`]
              }
              scaleType={scaleType}
              divBinnedMidValue={
                (scaleType === ScaleTypes.Diverging ||
                  scaleType === ScaleTypes.DivergingReverse ||
                  scaleType === ScaleTypes.Diverging_Reverse) &&
                legendType === LegendType.Binned
                  ? mid
                  : undefined
              }
              unitMeasure={filteredIndicatorData?.[0]?.unitMeasure}
              decimals={filteredIndicatorData?.[0]?.decimals}
              legendValuesForStd={legendValuesForStd}
              noDataColor={
                missingDataColor ? missingDataColor : COLOR_MAP.not_available
              }
            />
          ) : (
            <MapLegend
              legendRange={legendRange}
              showLegend={filteredIndicatorData?.length ? true : false}
              colors={
                scaleType === ScaleTypes.Categorical
                  ? COLOR_MAP['categorical']
                  : COLOR_MAP[`${scaleType.replace(' ', '')}_${binCount}`]
              }
              scaleType={scaleType}
              isTheme={isTheme}
              divBinnedMidValue={
                (scaleType === ScaleTypes.Diverging ||
                  scaleType === ScaleTypes.DivergingReverse ||
                  scaleType === ScaleTypes.Diverging_Reverse) &&
                legendType === LegendType.Binned
                  ? mid
                  : undefined
              }
              unitMeasure={filteredIndicatorData?.[0]?.unitMeasure}
              decimals={filteredIndicatorData?.[0]?.decimals}
              noDataColor={
                missingDataColor ? missingDataColor : COLOR_MAP.not_available
              }
            />
          )}
        </>
      ) : (
        <ContinuousMapLegend
          min={min}
          mid={mid}
          max={max}
          scaleType={scaleType}
          unitMeasure={filteredIndicatorData?.[0]?.unitMeasure}
          decimals={filteredIndicatorData?.[0]?.decimals}
          noDataColor={
            missingDataColor ? missingDataColor : COLOR_MAP.not_available
          }
        />
      )}
    </div>
  )
}
export default WorldMapWrapper
